import React from 'react';
import { styled, Typography } from '@mui/material';
import { error, neutral, primary } from '_lex/_foundations/phxColors';

export type PhxBadgeCounterColor = 'default' | 'primary' | 'gray' | 'red';
export type PhxBadgeCounterSize = 'sm' | 'md';
export type PhxBadgeCounterBorder = 'circle' | 'rounded';

export interface PhxBadgeCounterProps {
  /**
   * The number to display in the badge
   */
  number: number;
  /**
   * The color variant of the badge
   * @default 'default'
   */
  color?: PhxBadgeCounterColor;
  /**
   * The size of the badge
   * @default 'md'
   */
  size?: PhxBadgeCounterSize;
  /**
   * The border variant of the badge
   * @default 'circle'
   */
  border?: PhxBadgeCounterBorder;
}

const getColorStyles = (color: PhxBadgeCounterColor) => {
  const colorMap: Record<PhxBadgeCounterColor, { bg: string; text: string; border: string }> = {
    default: {
      bg: 'transparent',
      text: neutral[900],
      border: neutral[200],
    },
    primary: {
      bg: primary[800],
      text: neutral[25],
      border: primary[800],
    },
    gray: {
      bg: neutral[100],
      text: neutral[900],
      border: neutral[200],
    },
    red: {
      bg: error[800],
      text: neutral[25],
      border: error[800],
    },
  };

  return colorMap[color];
};

const PlusSign = styled('sup')({
  fontSize: '12px',
  lineHeight: '1',
  verticalAlign: 'super',
});

const StyledBadge = styled('div', {
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'size' && prop !== 'border',
})<{ color: PhxBadgeCounterColor; size: PhxBadgeCounterSize; border: PhxBadgeCounterBorder }>(({ color, size, border }) => {
  const colorStyles = getColorStyles(color);
  const isSmall = size === 'sm';

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colorStyles.bg,
    color: colorStyles.text,
    border: `1px solid ${colorStyles.border}`,
    fontSize: isSmall ? 12 : 16,
    height: isSmall ? 24 : 30,
    minWidth: isSmall ? 24 : 30,
    padding: '6px 8px',
    fontWeight: 400,
    lineHeight: isSmall ? '14.4px' : '24px',
    borderRadius: border === 'circle' ? '100px' : '4px',
  };
});

export const PhxBadgeCounter: React.FC<PhxBadgeCounterProps> = ({ number, color = 'default', size = 'md', border = 'circle' }) => {
  const shouldShowPlus = number > 99;

  return (
    <StyledBadge
      className={`lex-badge-counter lex-badge-counter-${color} lex-badge-counter-${size}`}
      color={color}
      size={size}
      border={border}
    >
      <Typography variant='captionMd'>{number <= 99 ? number : '99'}</Typography>
      {shouldShowPlus && <PlusSign>+</PlusSign>}
    </StyledBadge>
  );
};
